import { useQuery } from "@apollo/client/react";
import { Box } from "@mui/material";
import { type GridCellParams, type GridRowProps } from "@mui/x-data-grid-pro";
import { ArrowRight } from "@pmag/icons";
import {
    type ColumnDefinition,
    Typography,
    useDataGridApi,
    useDataGridFiltering,
    useDataGridFullTextFilter,
    useDataGridPaging,
    useDataGridSorting,
} from "@pmag/ui-components";
import { Routes } from "@src/context/router/RouterContext";
import { GQLCompatibleArticleSortField, type GQLUsageListQuery, type GQLUsageListQueryVariables } from "@src/graphql.generated";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { useRouter } from "@src/hooks/useRouter";
import { useSupportedBrands } from "@src/hooks/useSupportedBrands";
import { resolveDataGridQueryInputVariables } from "@src/pages/compatibilityCheck/components/usageList/DataGrid.utils";
import { usageListQuery } from "@src/pages/compatibilityCheck/components/usageList/UsageList.gql";
import { type SessionFilterState, type ToolbarFormState } from "@src/pages/compatibilityCheck/components/usageList/UsageListFormSchema";
import { type FunctionComponent, useMemo } from "react";
import { defineMessage, useIntl } from "react-intl";

import { Toolbar, type ToolbarProps } from "./toolbar/Toolbar";
import { ArrowLinkWrapper, StyledDataGridRow, UsageListDataGrid } from "./UsageList.styles";
import { componentGroupTypeMap, createQueryFilterFromSessionState, isUsageListComponentGroupType } from "./UsageList.utils";

const emptyPlaceholder = defineMessage({ id: "sparePartsFinder.usageList.emptyPlaceholder", defaultMessage: "—" });

export const dataTestId = {
    box: "sparePartsFinder.usageList.box",
    dataGrid: "sparePartsFinder.usageList.dataGrid",
};

export interface UsageListProps {
    appliedFilter: SessionFilterState;
    onFilterChange: (filter: ToolbarFormState) => void;
}

const sessionStoragePrefix = "pmag-spare-parts-finder";

export const UsageList: FunctionComponent<UsageListProps> = ({ appliedFilter, onFilterChange }) => {
    const intl = useIntl();
    const { setRoute } = useRouter();
    const { setModelId, setComponentGroupId } = useGlobalState();
    const { supportedBrandIds } = useSupportedBrands();

    const columns = useMemo<Array<ColumnDefinition<GQLCompatibleArticleSortField, string>>>(() => {
        return [
            {
                align: "left",
                field: "modelYear",
                headerName: intl.formatMessage({
                    id: "sparePartsFinder.usageList.dataGrid.headerName.modelYear",
                    defaultMessage: "Model Year",
                }),
                type: "string",
                minWidth: 115,
                sortable: true,
                renderCell: ({ row: usageListItem }: GridCellParams) => (
                    <Typography variant="heading-09">{usageListItem.modelYear || intl.formatMessage(emptyPlaceholder)}</Typography>
                ),
                apiMapping: {
                    sortFieldName: GQLCompatibleArticleSortField.modelYear,
                },
            },
            {
                align: "left",
                field: "modelDescription",
                headerName: intl.formatMessage({
                    id: "sparePartsFinder.usageList.dataGrid.headerName.modelArticleDescription",
                    defaultMessage: "Model Name",
                }),
                type: "string",
                minWidth: 300,
                sortable: true,
                flex: 1,
                renderCell: ({ row: usageListItem }: GridCellParams) => (
                    <Typography variant="heading-09">{usageListItem.modelDescription || intl.formatMessage(emptyPlaceholder)}</Typography>
                ),
                apiMapping: {
                    sortFieldName: GQLCompatibleArticleSortField.modelDescription,
                },
            },
            {
                align: "left",
                field: "modelSpecification",
                headerName: intl.formatMessage({
                    id: "sparePartsFinder.usageList.dataGrid.headerName.modelVariant",
                    defaultMessage: "Model variant",
                }),
                type: "string",
                minWidth: 130,
                sortable: true,
                renderCell: ({ row: usageListItem }: GridCellParams) => (
                    <Typography variant="body-short-01" color="gray">
                        {usageListItem.modelSpecification || intl.formatMessage(emptyPlaceholder)}
                    </Typography>
                ),
                apiMapping: {
                    sortFieldName: GQLCompatibleArticleSortField.modelSpecification,
                },
            },
            {
                align: "left",
                field: "componentGroupDescription",
                headerName: intl.formatMessage({
                    id: "sparePartsFinder.usageList.dataGrid.headerName.componentGroupDescription",
                    defaultMessage: "Component sub group",
                }),
                type: "string",
                minWidth: 280,
                sortable: true,
                renderCell: ({ row: usageListItem }: GridCellParams) => {
                    const componentGroupTypeStatus = usageListItem.componentGroupType;

                    return (
                        <Box>
                            <Typography variant="body-short-01">
                                {usageListItem.componentGroupDescription || intl.formatMessage(emptyPlaceholder)}
                            </Typography>

                            {isUsageListComponentGroupType(componentGroupTypeStatus) && (
                                <Typography variant="helper-text-01" color="gray">
                                    {intl.formatMessage(componentGroupTypeMap[componentGroupTypeStatus])}
                                </Typography>
                            )}
                        </Box>
                    );
                },
                apiMapping: {
                    sortFieldName: GQLCompatibleArticleSortField.componentGroupDescription,
                },
            },
            {
                align: "left",
                field: "articleName",
                headerName: intl.formatMessage({
                    id: "sparePartsFinder.usageList.dataGrid.headerName.articleName",
                    defaultMessage: "Article & art. no.",
                }),
                type: "string",
                minWidth: 220,
                sortable: true,
                renderCell: ({ row: usageListItem }: GridCellParams) => {
                    return (
                        <Box>
                            <Typography variant="body-short-01">{usageListItem.articleName || intl.formatMessage(emptyPlaceholder)}</Typography>

                            <Typography variant="helper-text-01" color="gray">
                                {usageListItem.articleNumber || intl.formatMessage(emptyPlaceholder)}
                            </Typography>
                        </Box>
                    );
                },
                apiMapping: {
                    sortFieldName: GQLCompatibleArticleSortField.articleName,
                },
            },
            {
                align: "center",
                field: "componentGroupId",
                type: "string",
                width: 60,
                sortable: false,
                filterable: false,
                pinnable: true,
                resizable: false,
                renderCell: () => {
                    return (
                        <ArrowLinkWrapper>
                            <ArrowRight color="primary" />
                        </ArrowLinkWrapper>
                    );
                },
            },
        ];
    }, [intl]);

    const fullTextFilter = useDataGridFullTextFilter({ storage: "sessionStorage", storagePrefix: sessionStoragePrefix });
    const paging = useDataGridPaging({ storage: "sessionStorage", storagePrefix: sessionStoragePrefix });
    const filtering = useDataGridFiltering({ columns, storage: "sessionStorage", storagePrefix: sessionStoragePrefix });
    const sorting = useDataGridSorting({ columns, storage: "sessionStorage", storagePrefix: sessionStoragePrefix });

    const query = useQuery<GQLUsageListQuery, GQLUsageListQueryVariables>(usageListQuery, {
        skip: !appliedFilter.articleNumber && !appliedFilter.articleDescription,
        variables: {
            ...resolveDataGridQueryInputVariables({
                columns,
                filtering,
                fullTextFilter,
                paging,
                sorting,
                articleNumber: appliedFilter.articleNumber,
                articleDescription: appliedFilter.articleDescription,
            }),
            filter: createQueryFilterFromSessionState(appliedFilter, supportedBrandIds),
            languageCode: intl.locale === "de" ? "de" : "en",
        },
    });

    const dataGrid = useDataGridApi({
        columns,
        filtering,
        fullTextFilter,
        paging,
        query,
        sorting,
    });

    const onRowClick = (modelId: string, componentGroupId: string) => {
        setRoute(Routes.SubGroupDetail);
        setModelId(modelId);
        setComponentGroupId(componentGroupId);
    };

    const toolbarProps: ToolbarProps = {
        appliedFilter: appliedFilter,
        onFilterChange: onFilterChange,
        onSearchChange: fullTextFilter.setSearchText,
        searchText: fullTextFilter.searchText,
    };

    return !appliedFilter.articleNumber && !appliedFilter.articleDescription ? null : (
        <Box data-testid={dataTestId.box}>
            <UsageListDataGrid
                {...dataGrid}
                checkboxSelection={false}
                autoHeight
                getRowId={() => crypto.randomUUID()}
                showColumnFilterIcons={false}
                slots={{
                    ...dataGrid.slots,
                    toolbar: Toolbar,
                    row: (props: GridRowProps) => (
                        <StyledDataGridRow {...props} onClick={() => onRowClick(props.row?.modelId, props.row?.componentGroupId)} />
                    ),
                }}
                slotProps={{
                    ...dataGrid.slotProps,
                    toolbar: toolbarProps,
                }}
                columnVisibilityModel={{ componentGroupId: true }}
                rowHeight="tall"
                pinnedColumns={{
                    right: ["componentGroupId"],
                }}
                data-testid={dataTestId.dataGrid}
            />
        </Box>
    );
};
